import { convertToRaw, EditorState, type RawDraftContentState } from 'draft-js';

import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import MilanoteCellEditor from './MilanoteCellEditor';
import { KEY_CODES } from '../../../utils/keyboard/keyConstants';

type MilanoteEditorRefDraft = MilanoteEditorRef<EditorState> & {
    editor: {
        editor: {
            _onKeyDown: (event: KeyboardEvent) => void;
            editor: HTMLElement;
        };
    };
};

export default class DraftCellEditor extends MilanoteCellEditor<EditorState, RawDraftContentState> {
    getEditorInstance(): MilanoteEditorRefDraft | null {
        const { cellEditorRef } = this.milanoteHot.milanoteProps;
        return (cellEditorRef?.current as MilanoteEditorRefDraft | null) || null;
    }

    onEditorBeginEditing(_initialValue?: string, event?: Event): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        // Trigger compositionstart event to make sure that the editor is in composition mode.
        if (event && event.type === 'compositionstart') {
            const eventClone = new CompositionEvent(event.type, event as CompositionEventInit);
            cellEditor.editor.editor.editor.dispatchEvent(eventClone);
        }
    }

    onEditorChange(editorState: EditorState): void {
        this.content = convertToRaw(editorState.getCurrentContent());
    }

    getEditorValue(): string {
        return rawGetText(this.content);
    }

    onEditorFinishEditing(): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        const editorState = cellEditor.getEditorState();

        // If the editor is in composition mode, we need to synchronously get it out of composition mode so
        //  the editor can be updated prior to "finishEditing" being called on the cell.
        // To exit composition mode, we need to simulate a key press, otherwise the editor will wait for
        //  a 20ms delay before exiting the composition mode, by using the right arrow key, the event will
        //  have its default prevented by the DraftEditorCompositionHandler.
        // Once this composition mode is exited, the MilanoteEditor will save the editor state to this.content
        //  via the "this.setEditorState" in the saveData, which is called from the onChange.
        if (editorState && editorState.isInCompositionMode()) {
            const keyDownEvent = new KeyboardEvent('keydown', { key: 'ArrowRight', which: KEY_CODES.RIGHT_ARROW });
            cellEditor.editor.editor._onKeyDown(keyDownEvent);
            return;
        }

        // Just before the user finish editing, get the last updated editor state and save it to this.content,
        // this is to make sure that the latest changes are saved.
        this.onEditorChange(editorState);
    }

    clearEditorContent(): void {
        this.content = null;
    }

    setPlainTextContent(plainText: string, style?: string[]): void {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return;

        cellEditor.setPlainText(plainText, style);
    }

    getPlainTextContent(): string {
        const cellEditor = this.getEditorInstance();

        if (!cellEditor) return '';

        return cellEditor.getEditorState().getCurrentContent().getPlainText();
    }

    hasValidElement(): boolean {
        const cellEditor = this.getEditorInstance();
        return !!cellEditor;
    }

    setEditorState(newEditorState: EditorState): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return;
        cellEditor.onChange(newEditorState);
    }

    getEditorState(): EditorState {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return EditorState.createEmpty();
        return cellEditor.getEditorState();
    }
}
